import { useTranslation } from 'react-i18next';
import { ASSESSMENT_SUBJECT_SELF } from 'components/Assessment/Subject';
import { HEALTH_STATUS_HEALTHY, HEALTH_STATUS_WITH_SICKNESS } from 'components/Assessment/HealthStatus';

function renderParagraph1(
  t,
  {
    assessmentSubject,
    age,
    healthStatus,
  }
) {
  const resultText = [];
  let priorityScore = 0;
  if (age.value >= 45) {
    priorityScore += 10;
  }
  if (healthStatus.value === HEALTH_STATUS_WITH_SICKNESS) {
    priorityScore += 10;
  }

  // Question 2
  switch (true) {
    case (age <= 44):
      resultText.push(t(
        assessmentSubject.value === ASSESSMENT_SUBJECT_SELF
          ? 'although you are still young'
          : 'although the assessment subject is still young'
      ));
      break;
    case (age <= 64):
      resultText.push(t(
        assessmentSubject.value === ASSESSMENT_SUBJECT_SELF
          ? 'you are approaching your elderly stage'
          : 'the assessment subject is approaching your elderly stage'
      ));
      break;
    default:
      resultText.push(t(
        assessmentSubject.value === ASSESSMENT_SUBJECT_SELF
          ? 'you have arrived at your elderly stage'
          : 'the assessment subject has arrived at elderly stage'
      ));
      break;
  }

  // Question 3
  switch (healthStatus.value) {
    case HEALTH_STATUS_WITH_SICKNESS:
      resultText.push(t('diagnosed with a terminal illness'))
      break;
    case HEALTH_STATUS_HEALTHY:
    default:
      resultText.push(t('not diagnosed with a terminal illness'))
      break;
  }

  // Priority score
  switch (true) {
    case (priorityScore < 10):
      resultText.push(t("it's good for you to plan ahead your end of life journey"));
      break;
    default:
      resultText.push(t('you need to manage your end of life journey'));
  }

  return (
    <p>
      {resultText.join('')}
    </p>
  )
}

/**
 * Format consideration list
 *
 * @param {(msg: string) => string} t
 * @param {Object[]} considerations
 *
 * @returns {string}
 */
function formatConsiderationList(t, considerations) {
  if (considerations.length === 1) {
    return considerations[0].title;
  }
  if (considerations.length === 2) {
    return considerations.map((item) => item.title).join(t('list and'));
  }
  const intermediate = [
    ...considerations.slice(0, -2).map((item) => item.title),
    considerations.slice(-2).map((item) => item.title).join(t('list and')),
  ];
  return intermediate.join(t('list comma'));
}

function renderParagraph2(
  t,
  assessmentConsiderationsAvailable = [],
  {
    assessmentSubject,
    considerations,
  }
) {
  const considerationsHas = considerations.value.map((id) => (assessmentConsiderationsAvailable.find((item) => item.id === id)));

  if (considerationsHas.length) {
    const considerationsTextList = formatConsiderationList(t, considerationsHas);
    return (
      <>
        <p>{t(
          (
            assessmentSubject.value === ASSESSMENT_SUBJECT_SELF
              ? 'you have considered aspects including'
              : 'the assessment subject has considered aspects including'
          ),
          { aspects: considerationsTextList },
        )}</p>
        <ul>
          {considerationsHas.map((item) => {
            const remarks = (item.remarks != '') ? ` (${item.remarks})` : '';
            return (
              <li key={item.id}>
                <a href={item.href} target="_blank" rel="noreferrer">{item.title}</a>{remarks}
              </li>
            );
          })}
        </ul>
      </>
    )
  }

  return (
    <>
      <p>{t(
        assessmentSubject.value === ASSESSMENT_SUBJECT_SELF
          ? 'despite these, you have not considered any aspects'
          : 'despite these, the assessment subject has not considered any aspects'
      )}</p>
      <ul>
          {assessmentConsiderationsAvailable.map((item) => {
            const remarks = (item.remarks != '') ? ` (${item.remarks})` : '';
            return (
              <li key={item.id}>
                <a href={item.href} target="_blank" rel="noreferrer">{item.title}</a>{remarks}
              </li>
            );
          })}
      </ul>
    </>
  )
}

function renderParagraph3(
  t,
  {
    language,
    assessmentSubject,
    hasWorries,
    worries,
    discussedWithOthers,
  },
) {
  const resultText = [];
  if (hasWorries.value === true) {
    resultText.push(t(
      (
        assessmentSubject.value === ASSESSMENT_SUBJECT_SELF
          ? 'besides, you have worries like'
          : 'besides, the assessment subject has worries like'
      ),
      {
        worries: worries.value,
      }
    ));
  } else {
    resultText.push(t(
      (
        assessmentSubject.value === ASSESSMENT_SUBJECT_SELF
          ? 'besides, you have no worries'
          : 'besides, the assessment subject has no worries'
      )
    ));
  }

  if (discussedWithOthers.value === true) {
    resultText.push(t(
      (
        assessmentSubject.value === ASSESSMENT_SUBJECT_SELF
          ? 'lucky that you have discussed with family or others'
          : 'lucky that the assessment subject has discussed with family or others'
      )
    ));
  } else {
    resultText.push(t(
      assessmentSubject.value === ASSESSMENT_SUBJECT_SELF
        ? 'unfortunately, you have not discussed with family or others'
        : 'unfortunately, the assessment subject hs not discussed with family or others'
    ));
  }

  return (
    <p>
      {resultText.join(
        language.value === 'zh-hant' ? '' : ' '
      )}
    </p>
  );
}

export function Results({
  className = '',
  assessmentConsiderationsAvailable = [],
  ...answers
}) {
  const { t } = useTranslation();
  const paragraph1 = renderParagraph1(t, answers);
  const paragraph2 = renderParagraph2(t, assessmentConsiderationsAvailable, answers);
  const paragraph3 = renderParagraph3(t, answers);
  return (
    <div className={className}>
      <h2>{ t('results') }</h2>
      {paragraph1}
      {paragraph2}
      {paragraph3}
    </div>
  );
}
